import { useCallback, useEffect, useState } from 'react'
import { Loader2, Lock, Info } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import {
  resignLabel, resignTone, lastDayLabel, defaultLastDay, bkkToday,
  CONFIDENTIAL_TH, BEFORE_SUBMIT_TH, NOTICE_DAYS,
} from '../../lib/resignation.js'
import { fmtDay, fmtStamp } from '../../lib/warning.js'
import { decNotice, refreshNotice } from '../../lib/notice.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import OffboardSurvey from './OffboardSurvey.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * การลาออก — the body of the resignation card inside พื้นที่ของฉัน › ตั้งค่า.
 *
 * WHY IT LIVES IN ตั้งค่า RATHER THAN ITS OWN TAB.
 * Resigning is a once-in-a-career act, and it belongs beside the employment facts it
 * changes (EmploymentCard, immediately above, holds วันเริ่มงาน and วันสิ้นสุดการจ้าง)
 * rather than in a navigation row people read every day. It also keeps ลาออก well away
 * from ขอลา, which is one syllable from it in Thai and used constantly.
 *
 * Renders the card BODY only — MeSetting's own <Card> supplies the section shell, the
 * icon and the heading, so this matches the four cards around it exactly.
 *
 * Loads its own data: ตั้งค่า fetches the Employee record and has no reason to know
 * about resignations, and threading a second unrelated fetch through it would couple
 * two things that have nothing to do with each other.
 */
export default function ResignBlock() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/me/resignation')
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Unlike a warning, an accepted resignation IS read simply by opening the page: the
  // outcome is information, not a document that needs acknowledging.
  useEffect(() => {
    const unseen = rows.filter((r) => r.status !== 'pending' && !r.decision_seen_at)
    if (!unseen.length) return
    Promise.all(unseen.map((r) => postJson('/api/me/resignation', { action: 'seen', id: r.id }).catch(() => null)))
      .then(() => decNotice('resign_decided', unseen.length))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length])

  if (loading) {
    return <p className="text-[13px] text-slate-400"><Loader2 size={13} className="animate-spin inline" /> กำลังโหลด…</p>
  }

  return (
    <>
      {err && <p className="text-[13px] text-rose-600 mb-2">{err}</p>}
      <ResignForm rows={rows} onChanged={load} />
    </>
  )
}

function ResignForm({ rows, onChanged }) {
  const [filing, setFiling] = useState(false)
  const [lastDay, setLastDay] = useState(defaultLastDay())
  const [reason, setReason] = useState('')
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [withdrawing, setWithdrawing] = useState(null)

  async function file() {
    setConfirm(false)
    setBusy(true)
    setErr('')
    try {
      await postJson('/api/me/resignation', { last_working_date: lastDay, reason_short: reason.trim() })
      setFiling(false)
      setReason('')
      await onChanged?.()
      refreshNotice()
    } catch (e) {
      setErr(apiErrorText(e, 'ส่งหนังสือแจ้งลาออกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  async function withdraw(r) {
    setWithdrawing(null)
    try {
      await postJson('/api/me/resignation', { action: 'withdraw', id: r.id })
      await onChanged?.()
    } catch (e) {
      setErr(apiErrorText(e, 'ถอนไม่สำเร็จ'))
    }
  }

  return (
    <div>
      {rows.length === 0 && !filing && (
        <>
          <p className="text-[13px] text-slate-500 leading-relaxed">
            หากท่านตัดสินใจลาออก กรุณาแจ้งผ่านระบบนี้ เพื่อให้ฝ่ายบุคคลและผู้จัดการทราบล่วงหน้าและติดต่อกลับได้
          </p>
          <button
            type="button" onClick={() => setFiling(true)}
            className="mt-3 px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            แจ้งความประสงค์ลาออก
          </button>
        </>
      )}

      {filing && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">
              วันทำงานวันสุดท้าย <span className="font-normal text-slate-400">· ตั้งค่าเริ่มต้นไว้ที่ {NOTICE_DAYS} วันนับจากวันนี้</span>
            </span>
            <input type="date" value={lastDay} min={bkkToday()} onChange={(e) => setLastDay(e.target.value)} className={FIELD} />
          </label>
          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">
              เหตุผลโดยย่อ <span className="font-normal text-slate-400">· ไม่บังคับ</span>
            </span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={FIELD} placeholder="เช่น ย้ายกลับต่างจังหวัด" />
          </label>

          <div className="flex gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5">
            <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-slate-600 leading-relaxed">{BEFORE_SUBMIT_TH}</p>
          </div>

          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setFiling(false); setErr('') }} className="px-3.5 py-2 rounded-xl text-[13px] text-slate-500 hover:bg-slate-100">
              ยกเลิก
            </button>
            <button
              type="button" onClick={() => setConfirm(true)} disabled={busy || !lastDay}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-50"
            >
              {busy && <Loader2 size={13} className="animate-spin" />} ส่งหนังสือแจ้งลาออก
            </button>
          </div>
        </div>
      )}

      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 px-3.5 py-3 mb-2.5">
          <div className="flex items-start gap-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-medium text-slate-800">
                {lastDayLabel(r)} {fmtDay(r.last_working_date)}
              </div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">แจ้งเมื่อ {fmtStamp(r.filed_at || r.created_at)}</div>
              {r.reason_short && <div className="text-[12.5px] text-slate-500 mt-0.5 break-words">{r.reason_short}</div>}
            </div>
            <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 shrink-0 ${resignTone(r.status)}`}>
              {resignLabel(r.status)}
            </span>
          </div>

          {r.decision_note && (
            <p className="mt-2 text-[12.5px] text-slate-600 break-words">{r.decision_note}</p>
          )}

          {r.status !== 'withdrawn' && (
            <>
              <OffboardSurvey resignationId={r.id} />
              <div className="mt-2 flex justify-end">
                <button
                  type="button" onClick={() => setWithdrawing(r)}
                  className="text-[12px] text-slate-400 hover:text-rose-600"
                >
                  ถอนหนังสือแจ้งลาออก
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {confirm && (
        <ConfirmDialog
          title="ส่งหนังสือแจ้งลาออก?"
          message={`วันทำงานวันสุดท้ายที่ขอ ${fmtDay(lastDay)}\n\n${BEFORE_SUBMIT_TH}\n\nหลังจากส่งแล้ว ท่านจะเห็นแบบสอบถามก่อนพ้นสภาพ ซึ่งฝ่ายบุคคลเท่านั้นที่เห็นคำตอบ`}
          confirmLabel="ส่งหนังสือ"
          onConfirm={file}
          onCancel={() => setConfirm(false)}
        />
      )}

      {withdrawing && (
        <ConfirmDialog
          title="ถอนหนังสือแจ้งลาออก?"
          message="ฝ่ายบุคคลจะเห็นว่าท่านถอนหนังสือฉบับนี้แล้ว หากมีการบันทึกวันสิ้นสุดการจ้างไว้ในประวัติพนักงานแล้ว กรุณาแจ้งฝ่ายบุคคลให้แก้ไขด้วย"
          confirmLabel="ถอนหนังสือ"
          tone="danger"
          onConfirm={() => withdraw(withdrawing)}
          onCancel={() => setWithdrawing(null)}
        />
      )}

      {rows.length > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-slate-400">
          <Lock size={11} /> {CONFIDENTIAL_TH.split(' — ')[0]}
        </p>
      )}
    </div>
  )
}
